import { useEffect, useState } from "react";
import {Stack, Typography, Grid, Divider} from "@mui/material";
import { MethodPicker, Switch } from "../../../../utils/form";
import _ from "lodash";
import NavigationBar from "../../../../utils/wizard/navigation";
import { useFieldArray, useForm, FormProvider } from "react-hook-form";
import TemplatesMenu from "../../../utils/medicalDocument/templates/menu";
import defaultTemplates from "./defaultTemplates";
import { useApi, useHealthCenterRules } from "../../../../utils/hooks";
import CardSeleted from "../../../utils/medicalDocument/card/CardSeleted";
import LaborCertificateChip from "../../components/LaborCertificateChip";
import { LABOR_CERTIFICATE, captureLaborCertificateClick } from "../../laborCertificate";
import Promise from "bluebird";

const defaultOptions = [
  { label: "Manual", value: "manual" },
  { label: "Laboratorio", value: "laboratory", disabled: true },
  { label: "Estudio por imágenes", value: "nonLaboratory", disabled: true },
];

const templateType = "order";
const defaultMedicine = { text: "" };

const HivCheckbox = ({ form }) => {
  return <Switch form={form} name="hiv" label="Paciente HIV" size={12} />;
};

const MedicineForm = ({ onFinish, ...props }) => {
  const defaultTemplateContent = defaultMedicine.text;
  const [method, setMethod] = useState("manual");
  const [templates, setTemplates] = useState([]);
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  const [disabledAddMedicine, setDisabledAddMedicine] = useState(false);
  const [options, setOptions] = useState(defaultOptions);
  const templatesRules = useHealthCenterRules("templates");

  const api = useApi();
  const form = useForm({
    defaultValues: { medicines: [defaultMedicine] },
  });
  const { handleSubmit, control, getValues, setValue, watch } = form;
  const { fields, append, remove } = useFieldArray({
    control: control,
    name: "medicines",
  });
  const medicines = getValues("medicines");
  const currentForm = watch();

  useEffect(() => {
    const isDisabled =
      medicines.length === 3 && (medicines[2]?.text ?? "") !== "";

    setDisabledAddMedicine(isDisabled);
  }, [medicines, medicines[2]?.text]);

  const addMedicine = () => {
    if (
      medicines.length > 0 &&
      medicines.some(
        (medicine) => medicine.text === "" || medicine.text === "\n"
      )
    ) {
      return;
    } else {
      append(_.cloneDeep(defaultMedicine));
    }
};

  useEffect(() => {
    const templatesOrEmpty = (type) => api.templates(type).catch(() => []);

    Promise.all([templatesOrEmpty(templateType), templatesOrEmpty(`${templateType}-nlp`), templatesOrEmpty(`${templateType}-lp`)])
      .then(([ manualTemplates, nonLaboratory, laboratory ]) => {
        const defaultedManualTemplates = templatesRules?.search?.noDefaults ? manualTemplates : _.concat(manualTemplates, defaultTemplates);
        return { manual: defaultedManualTemplates, nonLaboratory, laboratory };
      })
      .tap(setTemplates)
      .tap(templates => {
        const optionsWIthProcedures = () => _.map(templates, (value, key) => {
          const option = _.find(options, { value: key });
          if (key == "manual" || _.isEmpty(value)) return option;
          return { ...option, disabled: _.isEmpty(value) };
        });
        setOptions(optionsWIthProcedures());
      });
  }, []);

  useEffect(() => {
    if (method != "laboratory") return;
    setSelectedTemplate(_.get(templates, "laboratory[0]"));
  }, [method]);

  const handleChangeTabs = (event, newValue) => {
    setMethod(newValue);
    form.reset();
  };

  const someMedicineSelected = _.some(
    currentForm.medicines,
    (medicine) => medicine.text
  );
  const canContinue = someMedicineSelected;

  const onSubmit = (medicines) => {
    onFinish(medicines);
    props.nextStep();
  };

  const handleLaborCertificateClick = () => {
    captureLaborCertificateClick(method);
    if (method !== "manual") {
      setMethod("manual");
    }
    const medicines = getValues("medicines");
    const emptyMedicineIndex = _.findIndex(
      medicines,
      (medicine) => medicine.text === "" || medicine.text === "\n"
    );

    if (emptyMedicineIndex !== -1) {
      setValue(`medicines[${emptyMedicineIndex}].text`, LABOR_CERTIFICATE.content);
    } else if (medicines.length < 3) {
      append({ text: LABOR_CERTIFICATE.content });
    }
  };

  const methodTemplates = _.get(templates, _.find(options, { value: method })?.value, []);

  return (
    <FormProvider {...form}>
      <form onSubmit={handleSubmit(onSubmit)} autoComplete="off">
        <Stack spacing="1rem">
          <Typography
            variant="title"
            sx={{
              display: "flex",
              justifyContent: "center",
              textAlign: "center",
            }}
          >
            Solicitud
          </Typography>

          <MethodPicker
            method={method}
            handleChangeTabs={handleChangeTabs}
            options={options}
          />

          <TemplatesMenu
            templates={methodTemplates}
            selectedTemplate={selectedTemplate}
            setSelectedTemplate={setSelectedTemplate}
            defaultContent={defaultTemplateContent}
            type={templateType}
            addMedicine={addMedicine}
            form={form}
            defaultMedicine={defaultMedicine}
            disabledAddMedicine={disabledAddMedicine}
            canCreate={method === "manual"}
            belowSearch={<LaborCertificateChip onClick={handleLaborCertificateClick} />}
          />

          <Stack>
            <Grid container spacing={2} minHeight={260}>
              {_(fields)
                .map((field, index) => (
                  <Grid item xs={12} sm={12} md={6} key={field.id}>
                    <CardSeleted
                      form={form}
                      name={`medicines[${index}]`}
                      method="manual"
                      required={index === 0}
                      remove={remove}
                      index={index}
                      selectedTemplate={selectedTemplate}
                      setSelectedTemplate={setSelectedTemplate}
                      lengthMedicines={medicines.length}
                      templateType={templateType}
                      defaultMedicine={defaultMedicine}
                      fields={fields}
                      defaultTemplateText="Nueva solicitud"
                    />
                  </Grid>
                ))
                .value()}
            </Grid>

            <Divider sx={{ mt: 2, mb: 2 }} />

            <Stack
              sx={{
                display: "flex",
                justifyContent: "center",
                flexDirection: { xs: "column", sm: "row" },
              }}
            >
              <HivCheckbox form={form} />
            </Stack>
          </Stack>
        </Stack>

        <NavigationBar nextDisabled={!canContinue} {...props} />
      </form>
    </FormProvider>
  );
};

export default MedicineForm;
